import {
  FloatingNextArrow,
  FloatingPrevArrow,
} from '@/components/CarouselControls';
import { InterruptionTile } from '@/components/InterruptionTile';
import { isInterruption, type RailItem } from '@/components/InterruptionTile/types';
import { ProductTile } from '@/components/ProductTile';
import * as Styled from '@/components/ProductCarousel/ProductCarousel.styled';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useTrackItemList } from '@/hooks/useTrackItemList';
import type { Ga4Item } from '@/types';
import { toGa4Item } from '@/utils/toGa4Item';
import { useMemo, useRef } from 'react';
import { CarouselItem } from 'react-aria-carousel';

interface SplitLayoutFeaturedProductRailData {
  key: string;
  heading?: string;
  // Products, optionally interleaved with interruption cells (in author order).
  products: RailItem[];
  showAddToBasket: boolean;
  showMoreLikeThis?: boolean;
  /** GA4 item_list_id - Optimizely ContentLink.ID. */
  contentId?: number;
}

/**
 * N-FP-14 supporting-products carousel. Server-rendered block emits a
 * `data-react-component="SplitLayoutFeaturedProductRail"` mount point beside the key product
 * (see Views/Shared/Components/SplitLayoutFeaturedProductBlock/Default.cshtml). The Razor shell
 * owns the heading/intro, so this only renders the rail. Reuses the canonical E-FP-03 ProductTile
 * and the floating arrow controls from the sitewide ProductCarousel.
 */
export const SplitLayoutFeaturedProductRail = ({
  data,
}: {
  data: SplitLayoutFeaturedProductRailData;
}) => {
  const isWideScreen = useMediaQuery('(width >= 64rem)');
  const isMidScreen = useMediaQuery('(width >= 58rem)');
  const isSmallScreen = useMediaQuery('(width < 34rem)');

  const getItemsPerPage = () => {
    if (isWideScreen) {
      return 3;
    }

    if (isMidScreen) {
      return 2.75;
    }

    if (isSmallScreen) {
      return 1.66;
    }

    return 2.25;
  };

  const itemsPerPage = getItemsPerPage();
  const products = data.products ?? [];
  const enableScrolling = products.length > itemsPerPage;
  const railRef = useRef<HTMLDivElement>(null);
  const listName = data.heading || 'split-featured-rail';
  const list = useMemo(
    () => ({
      name: data.heading || 'split-featured-rail',
      id: data.contentId ? String(data.contentId) : undefined,
    }),
    [data.heading, data.contentId],
  );

  // GA4: view_item_list on in-view + select_item on tile click.
  // Interruption cells are promotions, tracked separately.
  const ga4Items = useMemo(
    () =>
      products
        .map((item, index) =>
          isInterruption(item) ? null : toGa4Item(item, list, index),
        )
        .filter((item): item is Ga4Item => item !== null),
    [products, list],
  );
  const { onSelectItem } = useTrackItemList(railRef, list, ga4Items);

  if (products.length === 0) {
    return null;
  }

  return (
    <Styled.Carousel
      key={data.key}
      aria-label={
        data.heading
          ? `Supporting products: ${data.heading}`
          : 'Supporting products'
      }
      itemsPerPage={itemsPerPage}
      loop={enableScrolling ? 'infinite' : false}
      scrollPadding="5px"
      spaceBetweenItems={isWideScreen ? '22px' : '16px'}
    >
      <Styled.Track ref={railRef}>
        {enableScrolling && isWideScreen ? <FloatingPrevArrow /> : null}
        <Styled.Main>
          {products.map((item: RailItem, index: number) =>
            isInterruption(item) ? (
              <CarouselItem key={item.key}>
                <InterruptionTile data={item} />
              </CarouselItem>
            ) : (
              <CarouselItem key={item.sku ?? index}>
                <ProductTile product={item} index={index} hideCta={!data.showAddToBasket} showMoreLikeThis={data.showMoreLikeThis} listName={listName} onSelect={() =>
                    onSelectItem(toGa4Item(item, list, index), index)
                  } />
              </CarouselItem>
            ),
          )}
        </Styled.Main>
        {enableScrolling && isWideScreen ? <FloatingNextArrow /> : null}
      </Styled.Track>
    </Styled.Carousel>
  );
};
